/**
 * Hard Limits Panel
 * 
 * Displays all items rated -2 (Hard Limit) for a persona.
 * These items are excluded from Lust Witch draws and JOI Engine tasks.
 */

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, ChevronDown, ChevronUp, Loader2, AlertTriangle, Trash2, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { Link } from 'react-router-dom';
import { API_BASE } from '../utils/api';

function HardLimitsPanel({
    personaId,
    collapsible = true,
    defaultExpanded = false
}) {
    const { getAuthHeaders } = useAuth();

    const [expanded, setExpanded] = useState(defaultExpanded);
    const [loading, setLoading] = useState(true);
    const [limits, setLimits] = useState([]);
    const [error, setError] = useState(null);

    // Fetch hard limits when component mounts or expands
    useEffect(() => {
        const fetchLimits = async () => {
            if (!personaId) return;
            if (collapsible && !expanded) return; // Don't fetch until expanded

            setLoading(true);
            setError(null);

            try {
                const headers = await getAuthHeaders();
                const response = await fetch(`${API_BASE}/api/ratings/${personaId}`, { headers });

                if (!response.ok) {
                    throw new Error('Failed to fetch ratings');
                }

                const data = await response.json();

                // Filter to only hard limits (-2 ratings)
                const hardLimits = (data.ratings || []).filter(r => r.rating === -2);
                setLimits(hardLimits);
            } catch (err) {
                console.error('Error fetching hard limits:', err);
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchLimits();
    }, [personaId, expanded, collapsible, getAuthHeaders]);

    const handleRemoveLimit = async (ratingId, entityType, entityId) => {
        // Change rating from -2 to 0 (neutral)
        try {
            const headers = await getAuthHeaders();
            const response = await fetch(`${API_BASE}/api/ratings`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...headers
                },
                body: JSON.stringify({
                    persona_id: personaId,
                    entity_type: entityType,
                    entity_id: entityId,
                    rating: 0  // Reset to neutral
                })
            });

            if (response.ok) {
                // Remove from local state
                setLimits(prev => prev.filter(l => l.id !== ratingId));
            }
        } catch (err) {
            console.error('Error removing limit:', err);
        }
    };

    const formatEntityType = (type) => {
        const labels = {
            'kink': 'Kink',
            'sex_action': 'Action',
            'gear': 'Gear',
            'arousal_mechanic': 'Mechanic'
        };
        return labels[type] || type;
    };

    const getEntityLink = (limit) => {
        const routes = {
            'kink': `/kink/${limit.entity_id}`,
            'sex_action': `/action/${limit.entity_id}`,
            'gear': `/gear/${limit.entity_id}`,
            'arousal_mechanic': `/mechanic/${limit.entity_id}`
        };
        return routes[limit.entity_type] || '#';
    };

    const content = (
        <div className="pt-4">
            {loading ? (
                <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-wine-400" />
                </div>
            ) : error ? (
                <div className="p-3 bg-red-900/20 border border-red-500/30 text-red-400 text-sm">
                    {error}
                </div>
            ) : limits.length === 0 ? (
                <div className="text-center py-6 text-dark-400">
                    <Shield className="w-10 h-10 mx-auto mb-3 opacity-50" />
                    <p>No hard limits set yet.</p>
                    <p className="text-xs mt-1">Rate items as "Hard Limit" to add them here.</p>
                </div>
            ) : (
                <div className="space-y-2">
                    <p className="text-xs text-dark-400 mb-3">
                        These items are excluded from all session suggestions.
                    </p>

                    {limits.map((limit) => (
                        <div
                            key={limit.id}
                            className="flex items-center gap-3 p-3 bg-red-900/10 border border-red-500/20"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <span className="text-xs px-1.5 py-0.5 bg-dark-800 text-dark-300">
                                        {formatEntityType(limit.entity_type)}
                                    </span>
                                    <span className="text-sm text-cream truncate">
                                        {limit.entity_id.slice(0, 8)}...
                                    </span>
                                </div>
                                {limit.notes && (
                                    <p className="text-xs text-dark-400 mt-1 truncate">{limit.notes}</p>
                                )}
                            </div>

                            <Link
                                to={getEntityLink(limit)}
                                className="p-1.5 text-dark-400 hover:text-cream transition-colors"
                                title="View item"
                            >
                                <ExternalLink size={14} />
                            </Link>

                            <button
                                onClick={() => handleRemoveLimit(limit.id, limit.entity_type, limit.entity_id)}
                                className="p-1.5 text-dark-400 hover:text-red-400 transition-colors"
                                title="Remove hard limit"
                            >
                                <Trash2 size={14} />
                            </button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    if (!collapsible) {
        return (
            <div className="glass-card p-6 border border-red-700/30">
                <h3 className="text-lg font-display font-bold text-cream flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    Hard Limits
                    {limits.length > 0 && (
                        <span className="text-xs px-2 py-0.5 bg-red-900/30 text-red-300 ml-2">
                            {limits.length}
                        </span>
                    )}
                </h3>
                {content}
            </div>
        );
    }

    return (
        <div className="glass-card border border-red-700/20 overflow-hidden">
            <button
                onClick={() => setExpanded(!expanded)}
                className="w-full flex items-center justify-between p-4 hover:bg-red-950/10 transition-colors"
            >
                <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-red-400" />
                    <span className="font-display font-bold text-cream">Hard Limits</span>
                    {limits.length > 0 && (
                        <span className="text-xs px-2 py-0.5 bg-red-900/30 text-red-300">
                            {limits.length}
                        </span>
                    )}
                </div>
                {expanded ? (
                    <ChevronUp className="w-5 h-5 text-cream/40" />
                ) : (
                    <ChevronDown className="w-5 h-5 text-cream/40" />
                )}
            </button>

            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                    >
                        <div className="px-4 pb-4 border-t border-red-700/20">
                            {content}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}

export default HardLimitsPanel;
